import { useCallback, useEffect, useRef, useState } from "react";
import QrScanner from "qr-scanner";
import { isAxiosError } from "axios";
import Button from "@/components/Button/Button";
import QrScanIcon from "@/components/SVGComponents/QrScanIcon";
import SwitchCameraIcon from "@/components/SVGComponents/SwitchCameraIcon";
import eventService from "@/services/api/events";
import { combineClasses } from "@/lib/utils";
import toast from "@/lib/toast";
import EnterCodeModal from "./components/EnterCodeModal";
import styles from "./DoorCheckIn.module.scss";

type ScannerStatus = "idle" | "starting" | "scanning";

interface CheckInResult {
  code: string;
  state: "checking" | "success" | "error";
  message?: string;
}

// The backend's own message when there is one (e.g. "Ticket already
// used"), else a generic fallback.
const getApiErrorMessage = (error: unknown) =>
  (isAxiosError<{ message?: string }>(error) && error.response?.data?.message) || "Check-in failed. Try again.";

// Browsers only expose the camera on a secure origin (https:// or
// localhost) and only after the user allows it — each failure gets its own
// message instead of a generic one.
const getCameraErrorMessage = (error: unknown) => {
  if (!window.isSecureContext) return "The camera only works over HTTPS. Open this page on the secure (https://) link.";
  const name = error instanceof Error ? error.name : "";
  const text = String(error instanceof Error ? error.message : error).toLowerCase();
  if (name === "NotAllowedError" || text.includes("permission") || text.includes("denied")) {
    return "Camera access was blocked. Allow camera access for this site in your browser settings, then try again.";
  }
  if (name === "NotFoundError" || text.includes("camera not found") || text.includes("no camera")) {
    return "No camera was found on this device.";
  }
  if (name === "NotReadableError") return "The camera is being used by another app. Close it and try again.";
  return "Couldn't start the camera. Try again, or enter the code manually.";
};

// Mobile-only door check-in (the sidebar only lists it at mobile width —
// see `sidebar.mobileOnly` on its route). Tapping the QR tile opens the
// back camera; a scanned code (or one typed in manually) goes through
// `handleCode`, which marks the member's attendance.
const DoorCheckIn = () => {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const scannerRef = useRef<QrScanner | null>(null);
  const [status, setStatus] = useState<ScannerStatus>("idle");
  const [cameraError, setCameraError] = useState<string | null>(null);
  const [result, setResult] = useState<CheckInResult | null>(null);
  const [isManualEntryOpen, setIsManualEntryOpen] = useState(false);
  // Back camera by default (scanning another person's phone); the switch
  // button flips to the front one. Remembered for the next scan too.
  const [facingMode, setFacingMode] = useState<QrScanner.FacingMode>("environment");
  const [hasMultipleCameras, setHasMultipleCameras] = useState(false);
  const [isSwitchingCamera, setIsSwitchingCamera] = useState(false);
  // The scanner can decode the same QR again before `stop()` takes effect —
  // this keeps it to one request per check-in.
  const isSubmittingRef = useRef(false);

  const stopScanner = useCallback(() => {
    scannerRef.current?.stop();
    scannerRef.current?.destroy();
    scannerRef.current = null;
    setStatus("idle");
  }, []);

  // Releases the camera when leaving the page mid-scan.
  useEffect(() => stopScanner, [stopScanner]);

  // Every check-in — camera or manual — ends up here and is sent to
  // `POST /events/attendance/mark` (eventService.markAttendance).
  const handleCode = useCallback(
    async (rawCode: string) => {
      const code = rawCode.trim();
      if (!code || isSubmittingRef.current) return;
      isSubmittingRef.current = true;
      stopScanner();
      setCameraError(null);
      setResult({ code, state: "checking" });

      try {
        const response = await eventService.markAttendance(code);
        const message = response?.message || "Checked in";
        toast.success(message);
        setResult({ code, state: "success", message });
      } catch (error) {
        // privateRequest's response interceptor already shows the error
        // toast — this only keeps the reason visible on the result card.
        setResult({ code, state: "error", message: getApiErrorMessage(error) });
      } finally {
        isSubmittingRef.current = false;
      }
    },
    [stopScanner],
  );

  const startScanner = async () => {
    if (!videoRef.current || status !== "idle" || isSubmittingRef.current) return;
    setCameraError(null);
    setResult(null);
    setStatus("starting");

    const scanner = new QrScanner(videoRef.current, (result) => handleCode(result.data), {
      preferredCamera: facingMode,
      returnDetailedScanResult: true,
      maxScansPerSecond: 5,
    });
    scannerRef.current = scanner;

    try {
      await scanner.start();
      setStatus("scanning");
      // Only reliable once camera permission is granted (i.e. after start).
      QrScanner.listCameras()
        .then((cameras) => setHasMultipleCameras(cameras.length > 1))
        .catch(() => setHasMultipleCameras(false));
    } catch (error) {
      stopScanner();
      setCameraError(getCameraErrorMessage(error));
    }
  };

  const switchCamera = async () => {
    const scanner = scannerRef.current;
    if (!scanner || isSwitchingCamera) return;
    const nextFacingMode: QrScanner.FacingMode = facingMode === "environment" ? "user" : "environment";
    setIsSwitchingCamera(true);
    try {
      await scanner.setCamera(nextFacingMode);
      setFacingMode(nextFacingMode);
    } catch {
      setCameraError("Couldn't switch camera. Try again.");
    } finally {
      setIsSwitchingCamera(false);
    }
  };

  const isCameraOn = status !== "idle";

  return (
    <div className={styles.page}>
      {/* Desktop: this flow needs a phone camera. */}
      <div className={styles.desktopNotice}>
        <QrScanIcon className={styles.desktopNoticeIcon} />
        <p className={styles.desktopNoticeTitle}>Door check-in is available on mobile only</p>
        <p className={styles.desktopNoticeCaption}>Open the admin panel on your phone to scan member tickets.</p>
      </div>

      <div className={styles.mobileView}>
        <div className={styles.scanArea}>
          <button
            type="button"
            className={combineClasses(styles.scanTile, isCameraOn && styles.scanTileActive)}
            onClick={startScanner}
            disabled={isCameraOn}
            aria-label={isCameraOn ? "Scanning for a QR code" : "Open camera to scan a ticket"}
          >
            {/* Always mounted — qr-scanner needs the element before it starts. */}
            <video ref={videoRef} className={styles.video} muted playsInline aria-hidden="true" />
            <span className={combineClasses(styles.corner, styles.cornerTopLeft)} />
            <span className={combineClasses(styles.corner, styles.cornerTopRight)} />
            <span className={combineClasses(styles.corner, styles.cornerBottomLeft)} />
            <span className={combineClasses(styles.corner, styles.cornerBottomRight)} />
            {isCameraOn ? (
              <span className={styles.scanLine} />
            ) : (
              <span className={styles.qrGlyph} aria-hidden="true">
                <span className={styles.qrDot} />
                <span className={styles.qrDotSmall} />
                <span className={styles.qrDivider} />
                <span className={styles.qrDot} />
                <span className={styles.qrDotSmall} />
              </span>
            )}
          </button>

          {isCameraOn && (
            <div className={styles.scanControls}>
              {status === "scanning" && hasMultipleCameras && (
                <button
                  type="button"
                  className={styles.scanControlButton}
                  onClick={switchCamera}
                  disabled={isSwitchingCamera}
                >
                  <SwitchCameraIcon className={styles.scanControlIcon} />
                  {facingMode === "environment" ? "Front camera" : "Back camera"}
                </button>
              )}
              <button type="button" className={styles.scanControlButton} onClick={stopScanner}>
                {status === "starting" ? "Starting camera…" : "Cancel scan"}
              </button>
            </div>
          )}
        </div>

        <h2 className={styles.title}>Scan member ticket</h2>
        <p className={styles.subtitle}>
          {isCameraOn
            ? "Hold the QR inside the frame."
            : "Tap the frame to open the camera. Entry is logged to the attendance dashboard instantly."}
        </p>

        {cameraError && (
          <p className={styles.errorText} role="alert">
            {cameraError}
          </p>
        )}

        {result && (
          <div
            className={combineClasses(
              styles.resultCard,
              result.state === "success" && styles.resultSuccess,
              result.state === "error" && styles.resultError,
            )}
            role="status"
          >
            <span className={styles.resultLabel}>
              {result.state === "checking" ? "Checking in…" : result.state === "success" ? "Checked in" : "Check-in failed"}
            </span>
            <span className={styles.resultCode}>{result.code}</span>
            {result.message && result.state !== "checking" && (
              <span className={styles.resultMessage}>{result.message}</span>
            )}
            {result.state !== "checking" && (
              <Button outlined onClick={startScanner} className={styles.resultButton}>
                {result.state === "error" ? "TRY AGAIN" : "SCAN NEXT"}
              </Button>
            )}
          </div>
        )}

        <div className={styles.footer}>
          <Button onClick={() => setIsManualEntryOpen(true)} className={styles.manualButton}>
            ENTER CODE MANUALLY
          </Button>
        </div>
      </div>

      <EnterCodeModal
        isOpen={isManualEntryOpen}
        onClose={() => setIsManualEntryOpen(false)}
        onSubmit={handleCode}
      />
    </div>
  );
};

export default DoorCheckIn;
